/**
 * WP-V2-SG — StableScene: stajnia interaktywna + build mode (locationType='stable').
 * PLAN-V2 §6 Stable + §2.5 konie. Konsumuje: AnimatedDogAvatar, ObjectLayer, WorldCamera, WalkController.
 */
import Phaser from 'phaser';
import {
  GRID_COLS,
  GRID_ROWS,
  TILE_PX,
  type PlacedBlock,
} from '@pieskowo/shared';
import { BaseScene } from '../../core/BaseScene.js';
import { UiKit, MIN_TOUCH } from '../../core/UiKit.js';
import { getSceneRouter } from '../../core/SceneRouter.js';
import { balanceBus } from '../../core/balanceBus.js';
import { GridBoard } from '../../core/build/GridBoard.js';
import { BlockPalette } from '../../core/build/BlockPalette.js';
import {
  serializeLayout,
  deserializeLayout,
} from '../../core/build/LayoutSerializer.js';
import {
  AnimatedDogAvatar,
  OutfitStore,
  preloadAnchors,
} from '../../avatar/index.js';
import {
  ObjectLayer,
  playFx,
  type InteractEvent,
  type InteractTarget,
} from '../../interact/index.js';
import {
  setupWorld,
  follow,
  WalkController,
} from '../../core/world/index.js';
import { StableHorses } from './horses.js';
import {
  loadOwnedBlocks,
  loadStableLayout,
  postPetNpc,
  putStableLayout,
  recordVisit,
  saveLocalStable,
} from './stableApi.js';
import {
  blockColor,
  blockIconKind,
  blockLabelPl,
  blockTextureKey,
  defaultLayerForBlock,
  normalizeLayout,
  placeBlock,
  remainingOwned,
  removeBlockAt,
  stableBlockCatalog,
  type StableBlockIconKind,
} from './stableLayout.js';
import {
  STABLE_COLLIDERS,
  STABLE_WORLD_H,
  STABLE_WORLD_W,
  STABLE_HAY_DEF_ID,
  STABLE_BRUSH_DEF_ID,
  STABLE_HORSESHOE_DEF_ID,
  STABLE_QUEST_ID,
  feedHorseFromHand,
  brushHorse,
  nailHorseshoe,
  haulHayOnWheelbarrow,
  isFeedDef,
  stableObjectDefs,
  stablePlacements,
  stableSnapZones,
} from './objects.js';
import {
  INTERACT_BUS_EVENT,
  horseIdFromSnap,
  stableMissionFromInteract,
} from './missionHook.js';

type Mode = 'visit' | 'build';

export class StableScene extends BaseScene {
  static readonly KEY = 'Stable';

  private mode: Mode = 'visit';
  private horses: StableHorses | null = null;
  private avatar: AnimatedDogAvatar | null = null;
  private walk: WalkController | null = null;
  private layer: ObjectLayer | null = null;
  private unsubLayer: (() => void) | null = null;
  private fedHorses = new Set<string>();
  private statusHud: Phaser.GameObjects.Text | null = null;

  private board = new GridBoard();
  private palette = new BlockPalette();
  private originX = 0;
  private originY = 0;
  private layout: PlacedBlock[] = [];
  private layoutVersion = 0;
  private owned: string[] = [];
  private selectedId: string | null = null;
  private ghost: Phaser.GameObjects.Rectangle | null = null;
  private worldLayer: Phaser.GameObjects.Container | null = null;
  private paletteLayer: Phaser.GameObjects.Container | null = null;
  private uiLayer: Phaser.GameObjects.Container | null = null;
  private statusText: Phaser.GameObjects.Text | null = null;
  private saving = false;
  private dirty = false;
  private suppressPlace = false;
  private visitRoot: Phaser.GameObjects.Container | null = null;
  private buildDecor: Phaser.GameObjects.Container | null = null;

  constructor() {
    super(StableScene.KEY);
  }

  preload(): void {
    this.preloadRealBg('bg_stable');
    for (const pose of ['idle', 'walkA', 'walkB', 'sit', 'jump', 'wave'] as const) {
      const key = `dog_maltese_${pose}`;
      if (!this.textures.exists(key)) {
        this.load.image(key, `/assets/real/maltese_${pose}.png`);
      }
    }
    if (!this.textures.exists('ph_dog')) {
      this.load.image('ph_dog', '/assets/placeholder/dog.png');
    }
    // Obiekty stajni (Higgsfield) — klucz == nazwa pliku; brak → fallback proceduralny.
    for (const o of ['apple', 'brush', 'bucket', 'carrot', 'hay', 'horseshoe', 'ladder', 'wheelbarrow']) {
      this.load.image(`obj_stable_${o}`, `/assets/real/obj_stable_${o}.png`);
    }
    void preloadAnchors(this);
  }

  create(): void {
    this.cameras.main.setBackgroundColor(0xc8e6c9);
    this.originX = (this.gameW - GRID_COLS * TILE_PX) / 2;
    this.originY = 56;

    const loaded = loadStableLayout();
    this.layout = loaded.layout;
    this.layoutVersion = loaded.layoutVersion;
    this.owned = loadOwnedBlocks();
    this.palette.setOwned(this.owned);

    this.uiLayer = this.add.container(0, 0).setDepth(30_000);
    this.uiLayer.setScrollFactor(0);
    this.statusText = this.add
      .text(this.gameW / 2, this.gameH - 22, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#4a3f55',
      })
      .setOrigin(0.5)
      .setDepth(30_100)
      .setScrollFactor(0);

    this.enterVisitMode();
    void recordVisit('stable');

    this.events.once('shutdown', () => this.teardown());
    this.events.once('destroy', () => this.teardown());
  }

  update(_t: number, dtMs: number): void {
    if (this.mode !== 'visit') return;
    const dtSec = Math.min(0.05, dtMs / 1000);
    this.walk?.update(dtSec);
    const pos = this.walk?.position;
    this.avatar?.update(dtSec);
    if (pos && this.avatar) {
      this.avatar.container.setPosition(pos.x, pos.y);
    }
    this.layer?.update(dtSec);
    this.horses?.update(dtSec);
    // aktualizuj snap strefy koni
    if (this.horses && this.layer) {
      const posList = this.horses.getPositions();
      const a = posList.find((p) => p.snapZoneId === 'stable_horse_a');
      const b = posList.find((p) => p.snapZoneId === 'stable_horse_b');
      this.layer.setSnapZones(
        stableSnapZones(
          a ? { x: a.x, y: a.y } : undefined,
          b ? { x: b.x, y: b.y } : undefined,
        ),
      );
    }
  }

  private teardown(): void {
    this.teardownVisitEngines();
    this.horses?.destroy();
    this.horses = null;
    this.input.off('pointermove');
    this.input.off('pointerdown');
  }

  private teardownVisitEngines(): void {
    this.unsubLayer?.();
    this.unsubLayer = null;
    this.layer?.destroy();
    this.layer = null;
    this.walk?.destroy();
    this.walk = null;
    this.avatar?.destroy();
    this.avatar = null;
    this.statusHud?.destroy();
    this.statusHud = null;
    this.registry.remove('companionFollow');
  }

  private clearModeLayers(): void {
    this.teardownVisitEngines();
    this.horses?.destroy();
    this.horses = null;
    this.visitRoot?.destroy(true);
    this.visitRoot = null;
    this.buildDecor?.destroy(true);
    this.buildDecor = null;
    this.worldLayer?.destroy(true);
    this.worldLayer = null;
    this.paletteLayer?.destroy(true);
    this.paletteLayer = null;
    this.ghost?.destroy();
    this.ghost = null;
    this.uiLayer?.removeAll(true);
    this.input.off('pointermove');
    this.input.off('pointerdown');
    // reset kamery do ekranu gry w build mode
    this.cameras.main.setBounds(0, 0, this.gameW, this.gameH);
    this.cameras.main.stopFollow();
    this.cameras.main.setScroll(0, 0);
  }

  // ── Visit mode ──────────────────────────────────────────────────────

  private enterVisitMode(): void {
    this.mode = 'visit';
    this.clearModeLayers();
    this.fedHorses = new Set();

    setupWorld(this, {
      worldW: STABLE_WORLD_W,
      worldH: STABLE_WORLD_H,
      bg: 0xc8e6c9,
    });
    this.drawVisitWorld();
    this.spawnPlayer();
    this.spawnHorsesAndLayer();
    this.paintVisitNav();
    this.setStatus('Weź siano → nakarm konika z ręki · szczotka · taczka · buduj');
    this.registry.set('companionFollow', {
      x: STABLE_WORLD_W / 2,
      y: 420,
    });
  }

  private drawVisitWorld(): void {
    this.visitRoot = this.add.container(0, 0).setDepth(10);

    if (this.textures.exists('bg_stable')) {
      this.add
        .image(STABLE_WORLD_W / 2, STABLE_WORLD_H / 2, 'bg_stable')
        .setDisplaySize(STABLE_WORLD_W, STABLE_WORLD_H)
        .setDepth(-1000);
    }

    const barn = this.add
      .rectangle(STABLE_WORLD_W / 2, 160, 520, 120, 0xbcaaa4)
      .setStrokeStyle(4, 0x8d6e63);
    const roof = this.add.triangle(
      STABLE_WORLD_W / 2,
      90,
      0,
      60,
      280,
      60,
      140,
      0,
      0xe57373,
    );
    const title = this.add
      .text(STABLE_WORLD_W / 2, 28, 'Stajnia', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '36px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(20_000);

    const padX = 780;
    const padY = 280;
    const padW = 560;
    const padH = 280;
    const paddock = this.add
      .rectangle(padX + padW / 2, padY + padH / 2, padW, padH, 0xa5d6a7, 0.55)
      .setStrokeStyle(6, 0x8d6e63);

    const hint = this.add
      .text(STABLE_WORLD_W / 2, 72, 'Misja: nakarm oba koniki 🌾', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#6b5f7a',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(20_000);

    this.visitRoot.add([barn, roof, paddock]);
    this.statusHud = hint;
    this.paintVisitLayoutPreview();
  }

  private spawnPlayer(): void {
    const cache = OutfitStore.load();
    const breed =
      cache.breed || (this.registry.get('dogBreed') as string) || 'maltese';
    const color =
      cache.color || (this.registry.get('dogColor') as string) || undefined;
    const startX = 240;
    const startY = 480;
    this.avatar = new AnimatedDogAvatar({
      scene: this,
      x: startX,
      y: startY,
      breed,
      color,
      slots: cache.slots,
      swimSlots: cache.swimSlots,
      mode: 'normal',
      depth: 100,
    });

    this.walk = new WalkController(this, this.avatar, {
      colliders: STABLE_COLLIDERS,
      bounds: {
        minX: 48,
        minY: 140,
        maxX: STABLE_WORLD_W - 48,
        maxY: STABLE_WORLD_H - 48,
      },
      enableInput: true,
    });

    follow(this, this.avatar.container, {
      lerp: 0.12,
      deadzone: { width: 100, height: 70 },
    });
  }

  private spawnHorsesAndLayer(): void {
    this.horses = new StableHorses(this, {
      onPetApi: (id) => {
        void this.handlePetHorse(id);
      },
      onFed: (id) => this.onHorseFed(id),
    });
    this.horses.setBounds({
      minX: 800,
      minY: 320,
      maxX: 1300,
      maxY: 520,
    });
    this.horses.spawn();

    const self = this;
    const playerTarget: InteractTarget = {
      moveTo: (x, y) => {
        self.walk?.moveTo(x, y);
      },
      holdItem: (tex) => {
        self.avatar?.holdItem(tex);
      },
      playAction: (a) => {
        if (!self.avatar) return Promise.resolve();
        if (a === 'sniff') return self.avatar.playAction('happy');
        const known = a as 'sit' | 'jump' | 'wave' | 'eat' | 'swim' | 'sleep' | 'happy';
        return self.avatar.playAction(known);
      },
      getPosition: () =>
        self.walk?.position ?? { x: self.avatar?.x ?? 0, y: self.avatar?.y ?? 0 },
      get isMoving() {
        return self.walk?.isMoving ?? false;
      },
      get x() {
        return self.walk?.position.x ?? self.avatar?.x ?? 0;
      },
      get y() {
        return self.walk?.position.y ?? self.avatar?.y ?? 0;
      },
    };

    this.layer = new ObjectLayer({
      scene: this,
      defs: stableObjectDefs(),
      target: playerTarget,
      giveTargets: this.horses.getGiveTargets(),
      interactRange: 100,
      groundBounds: {
        minX: 48,
        maxX: STABLE_WORLD_W - 48,
        minY: 360,
        maxY: STABLE_WORLD_H - 48,
      },
    });
    this.layer.setSnapZones(stableSnapZones());
    this.layer.spawn(stablePlacements());
    this.unsubLayer = this.layer.on((evt) => this.onInteract(evt));
  }

  private onInteract(evt: InteractEvent): void {
    this.game.events.emit(INTERACT_BUS_EVENT, {
      sceneKey: StableScene.KEY,
      ...evt,
    });
    this.events.emit('interact', evt);

    // give / consume siana lub marchewki → karmienie
    if (
      (evt.type === 'give' || evt.type === 'consume') &&
      isFeedDef(evt.defId)
    ) {
      const horseId =
        horseIdFromSnap(evt.snapZoneId) ??
        this.pickNearestHorseId(evt.x, evt.y);
      if (horseId) {
        this.applyFeed(horseId, evt);
      }
    }

    // snap siana na konia / taczkę
    if (evt.type === 'snap') {
      if (haulHayOnWheelbarrow(evt.snapZoneId, evt.defId)) {
        playFx(this, 'sparkle', evt.x, evt.y);
        this.setStatus('Siano na taczce — wio! 🛒');
      }
      const hid = horseIdFromSnap(evt.snapZoneId);
      if (hid && isFeedDef(evt.defId)) {
        this.applyFeed(hid, evt);
      }
    }

    if (evt.type === 'tap' || evt.type === 'toggle') {
      if (evt.defId === STABLE_BRUSH_DEF_ID) {
        const r = brushHorse(evt.defId);
        if (r.hearts) {
          this.horses?.brushNearest(evt.x, evt.y);
          this.setStatus('Czesanie — serduszka! 💕');
        }
      }
      if (evt.defId === STABLE_HORSESHOE_DEF_ID) {
        const r = nailHorseshoe(evt.defId);
        if (r.sound) {
          playFx(this, 'stars', evt.x, evt.y);
          playFx(this, 'notes', evt.x, evt.y);
          this.setStatus('Przybita podkowa — szczęście! 🧲');
        }
      }
    }
  }

  private async handlePetHorse(horseId: string): Promise<void> {
    this.setStatus('Uśmiech konika! 💖');
    const res = await postPetNpc(horseId);
    if (!this.sys.isActive()) return;
    if (res.ok) {
      balanceBus.set(res.data.balance);
      if (res.data.rewardZl > 0) {
        this.setStatus(`Uśmiech konika! 💖 +${res.data.rewardZl} zł`);
      }
      return;
    }
    // Głaskanie i tak się udało wizualnie — nagroda wymaga serwera.
    if (res.code === 'UNAUTHORIZED') {
      this.setStatus('Konik Cię lubi! 💖 Zaloguj się, by zbierać złotówki');
    } else if (res.code === 'NETWORK') {
      this.setStatus('Konik Cię lubi! 💖 Złotówki doliczymy po połączeniu');
    }
  }

  private pickNearestHorseId(x: number, y: number): string | null {
    const list = this.horses?.getPositions() ?? [];
    if (!list.length) return null;
    let best = list[0]!;
    let bestD = Infinity;
    for (const p of list) {
      const d = Math.hypot(p.x - x, p.y - y);
      if (d < bestD) {
        bestD = d;
        best = p;
      }
    }
    return best.id;
  }

  private applyFeed(horseId: string, evt: InteractEvent): void {
    const result = feedHorseFromHand(horseId, this.fedHorses, evt.defId);
    if (!result.ok) return;
    this.fedHorses = new Set(result.fedIds);
    this.horses?.feedById(horseId);

    const mission = stableMissionFromInteract(evt, horseId);
    if (mission) {
      this.game.events.emit(INTERACT_BUS_EVENT, mission);
      this.events.emit('quest-progress', mission);
    }

    this.onHorseFed(horseId, result.missionComplete);
  }

  private onHorseFed(horseId: string, missionComplete?: boolean): void {
    this.fedHorses.add(horseId);
    const done =
      missionComplete ?? this.fedHorses.size >= 2;
    if (done) {
      this.setStatus('Misja: oba koniki nakarmione! ✨');
      this.game.events.emit(INTERACT_BUS_EVENT, {
        sceneKey: StableScene.KEY,
        questId: STABLE_QUEST_ID,
        stepKind: 'feed_horse_complete',
        fedIds: [...this.fedHorses],
      });
    } else {
      this.setStatus(
        `Nakarmiono! (${this.fedHorses.size}/2) — jeszcze jeden konik 🌾`,
      );
    }
  }

  private paintVisitLayoutPreview(): void {
    if (!this.visitRoot || this.layout.length === 0) return;
    const scale = 0.28;
    const ox = 100;
    const oy = 200;
    const label = this.add
      .text(ox + 80, oy - 20, 'Twój domek w stajni', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#5d4037',
      })
      .setOrigin(0.5);
    this.visitRoot.add(label);
    const sorted = [...this.layout].sort((a, b) => a.layer - b.layer || a.y - b.y);
    for (const b of sorted) {
      const x = ox + b.x * TILE_PX * scale;
      const y = oy + b.y * TILE_PX * scale;
      const r = this.add
        .rectangle(x, y, TILE_PX * scale - 2, TILE_PX * scale - 2, blockColor(b.id))
        .setStrokeStyle(1, 0xffffff);
      this.visitRoot.add(r);
    }
  }

  private paintVisitNav(): void {
    if (!this.uiLayer) return;
    const back = UiKit.button(this, {
      x: this.gameW / 2 - 160,
      y: this.gameH - 70,
      label: 'Wróć do miasteczka',
      fill: 0xffcc80,
      width: 240,
      onClick: () => getSceneRouter().go('Town'),
    });
    back.setScrollFactor(0);
    const build = UiKit.button(this, {
      x: this.gameW / 2 + 150,
      y: this.gameH - 70,
      label: 'Buduj domek',
      fill: 0x80deea,
      width: 220,
      onClick: () => this.enterBuildMode(),
    });
    build.setScrollFactor(0);
    this.uiLayer.add([back, build]);
  }

  // ── Build mode (zachowany WP9) ──────────────────────────────────────

  private enterBuildMode(): void {
    this.mode = 'build';
    this.clearModeLayers();
    this.cameras.main.setBackgroundColor(0xdcedc8);
    if (this.textures.exists('bg_stable')) {
      this.add
        .image(this.gameW / 2, this.gameH / 2, 'bg_stable')
        .setDisplaySize(this.gameW, this.gameH)
        .setDepth(-1000)
        .setScrollFactor(0);
    }

    this.buildDecor = this.add.container(0, 0).setDepth(5);
    const title = this.add
      .text(this.gameW / 2, 22, 'Budowanie w stajni', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '28px',
        color: '#5d4037',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.buildDecor.add(title);

    this.owned = loadOwnedBlocks();
    this.palette.setOwned(this.owned);
    this.selectedId = null;

    this.drawGrid();
    this.worldLayer = this.add.container(0, 0).setDepth(20);
    this.paletteLayer = this.add.container(0, 0).setDepth(30);
    this.ghost = this.add
      .rectangle(0, 0, TILE_PX - 4, TILE_PX - 4, 0xffffff, 0.35)
      .setStrokeStyle(2, 0x4caf50)
      .setVisible(false)
      .setDepth(50);

    this.paintBlocks();
    this.paintPalette();
    this.paintBuildChrome();
    this.bindBuildInput();
    this.setStatus('Wybierz klocek stajenny, potem stuknij siatkę');
  }

  private drawGrid(): void {
    const g = this.add.graphics();
    g.fillStyle(0xfff8e1, 1);
    g.fillRect(
      this.originX,
      this.originY,
      GRID_COLS * TILE_PX,
      GRID_ROWS * TILE_PX,
    );
    g.lineStyle(1, 0xa1887f, 0.5);
    for (let x = 0; x <= GRID_COLS; x++) {
      const wx = this.originX + x * TILE_PX;
      g.lineBetween(wx, this.originY, wx, this.originY + GRID_ROWS * TILE_PX);
    }
    for (let y = 0; y <= GRID_ROWS; y++) {
      const wy = this.originY + y * TILE_PX;
      g.lineBetween(this.originX, wy, this.originX + GRID_COLS * TILE_PX, wy);
    }
    this.buildDecor?.add(g);
  }

  private tileCenter(tx: number, ty: number): { x: number; y: number } {
    const w = this.board.tileToWorld(tx, ty);
    return { x: this.originX + w.x, y: this.originY + w.y };
  }

  private pointerToTile(
    px: number,
    py: number,
  ): { tx: number; ty: number } | null {
    const localX = px - this.originX;
    const localY = py - this.originY;
    const { tx, ty } = this.board.worldToTile(localX, localY);
    if (!this.board.inBounds(tx, ty)) return null;
    return { tx, ty };
  }

  /** Proceduralne ikony klocków stajennych (bez emoji) — generateTexture. */
  private ensureBlockIconTexture(id: string): string {
    const key = blockTextureKey(id);
    if (this.textures.exists(key)) return key;
    const kind = blockIconKind(id);
    const g = this.make.graphics({ x: 0, y: 0 });
    drawStableBlockIcon(g, kind, STABLE_ICON_TEX, STABLE_ICON_TEX);
    g.generateTexture(key, STABLE_ICON_TEX, STABLE_ICON_TEX);
    g.destroy();
    return key;
  }

  private paintBlocks(): void {
    this.worldLayer?.removeAll(true);
    const sorted = [...this.layout].sort((a, b) => a.layer - b.layer || a.y - b.y);
    for (const b of sorted) {
      const { x, y } = this.tileCenter(b.x, b.y);
      const rect = this.add
        .rectangle(x, y, TILE_PX - 6, TILE_PX - 6, blockColor(b.id))
        .setStrokeStyle(2, 0xffffff)
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true });
      rect.on('pointerdown', () => {
        this.suppressPlace = true;
        this.layout = removeBlockAt(this.layout, b.x, b.y, b.layer);
        this.dirty = true;
        this.selectedId = b.id;
        this.paintBlocks();
        this.paintPalette();
        this.setStatus(`Schowano: ${blockLabelPl(b.id)} (do pudełka)`);
      });
      const tex = this.ensureBlockIconTexture(b.id);
      const icon = this.add
        .image(x, y - 2, tex)
        .setDisplaySize(30, 30)
        .setOrigin(0.5);
      this.worldLayer?.add([rect, icon]);
    }
  }

  private paintPalette(): void {
    this.paletteLayer?.removeAll(true);
    const catalog = stableBlockCatalog();
    const ids = catalog.map((c) => c.id);
    const itemW = Math.max(MIN_TOUCH + 8, 72);
    const gap = 10;
    const totalW = ids.length * (itemW + gap) - gap;
    const startX = (this.gameW - totalW) / 2 + itemW / 2;
    const y = this.originY + GRID_ROWS * TILE_PX + 44;

    ids.forEach((id, i) => {
      const left = remainingOwned(this.owned, this.layout, id);
      const x = startX + i * (itemW + gap);
      const selected = this.selectedId === id;
      const bg = this.add
        .rectangle(x, y, itemW, MIN_TOUCH + 12, selected ? 0x80deea : 0xfff3e0)
        .setStrokeStyle(selected ? 4 : 2, selected ? 0x00838f : 0xffffff)
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true });
      const tex = this.ensureBlockIconTexture(id);
      const icon = this.add
        .image(x, y - 10, tex)
        .setDisplaySize(28, 28)
        .setOrigin(0.5);
      const qty = this.add
        .text(x, y + 18, `${left}×`, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '14px',
          color: left > 0 ? '#4a3f55' : '#b71c1c',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
      bg.on('pointerdown', () => {
        this.selectedId = id;
        this.paintPalette();
        this.setStatus(`Wybrano: ${blockLabelPl(id)} — stuknij siatkę`);
      });
      this.paletteLayer?.add([bg, icon, qty]);
    });
  }

  private paintBuildChrome(): void {
    if (!this.uiLayer) return;
    const back = UiKit.button(this, {
      x: 110,
      y: this.gameH - 70,
      label: 'Wróć',
      fill: 0xffcc80,
      width: 160,
      onClick: () => {
        if (this.dirty) saveLocalStable(this.layout, this.layoutVersion);
        this.enterVisitMode();
      },
    });
    const save = UiKit.button(this, {
      x: this.gameW - 110,
      y: this.gameH - 70,
      label: 'Zapisz',
      fill: 0xa5d6a7,
      width: 160,
      onClick: () => {
        void this.saveBuild();
      },
    });
    this.uiLayer.add([back, save]);
  }

  private bindBuildInput(): void {
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (this.mode !== 'build' || !this.selectedId || !this.ghost) return;
      const tile = this.pointerToTile(p.x, p.y);
      if (!tile) {
        this.ghost.setVisible(false);
        return;
      }
      const { x, y } = this.tileCenter(tile.tx, tile.ty);
      this.ghost.setPosition(x, y);
      this.ghost.setFillStyle(blockColor(this.selectedId), 0.4);
      this.ghost.setVisible(true);
    });

    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      if (this.mode !== 'build') return;
      if (this.suppressPlace) {
        this.suppressPlace = false;
        return;
      }
      if (!this.selectedId) return;
      if (p.y > this.originY + GRID_ROWS * TILE_PX) return;
      const tile = this.pointerToTile(p.x, p.y);
      if (!tile) return;

      const layer = defaultLayerForBlock(this.selectedId);
      const result = placeBlock(
        this.layout,
        { id: this.selectedId, x: tile.tx, y: tile.ty, layer },
        this.owned,
      );
      if (!result.ok) {
        if (result.reason === 'overlap') {
          this.layout = removeBlockAt(this.layout, tile.tx, tile.ty, layer);
          const retry = placeBlock(
            this.layout,
            { id: this.selectedId, x: tile.tx, y: tile.ty, layer },
            this.owned,
          );
          if (retry.ok) {
            this.layout = retry.layout;
            this.dirty = true;
            this.paintBlocks();
            this.paintPalette();
            this.setStatus(`Postawiono: ${blockLabelPl(this.selectedId!)}`);
            return;
          }
        }
        this.setStatus(this.reasonPl(result.reason));
        return;
      }
      this.layout = result.layout;
      this.dirty = true;
      this.paintBlocks();
      this.paintPalette();
      this.setStatus(`Postawiono: ${blockLabelPl(this.selectedId)}`);
    });
  }

  private reasonPl(reason: string): string {
    switch (reason) {
      case 'overlap':
        return 'Tu już coś stoi — stuknij klocek, by schować';
      case 'qty':
        return 'Koniec klocków — zbierz więcej w sklepie';
      case 'oob':
        return 'Poza siatką stajni';
      case 'too_many':
        return 'Za dużo klocków — schowaj coś do pudełka';
      default:
        return 'Nie da się tu postawić';
    }
  }

  private async saveBuild(): Promise<void> {
    if (this.saving) return;
    this.saving = true;
    this.setStatus('Zapisuję domek w stajni…');

    const raw = serializeLayout(this.layout);
    const roundTrip = deserializeLayout(raw);
    this.layout = normalizeLayout(roundTrip);
    saveLocalStable(this.layout, this.layoutVersion);

    const remote = await putStableLayout(this.layout, this.layoutVersion);
    if (!this.sys.isActive()) return;

    if (remote.ok) {
      this.layoutVersion = remote.version;
      this.dirty = false;
      saveLocalStable(this.layout, this.layoutVersion);
      this.setStatus('Zapisano domek w stajni! ✨');
      this.saving = false;
      return;
    }

    if (remote.code === 'VERSION_CONFLICT' || remote.status === 409) {
      this.setStatus('Wersja się nie zgadza — zapis lokalny zostaje, spróbuj później');
    } else if (remote.code === 'NETWORK' || remote.status === 0) {
      this.setStatus('Zapisano u Ciebie (offline) — wyślesz później');
      this.dirty = false;
    } else if (remote.code === 'UNAUTHORIZED' || remote.status === 401) {
      this.setStatus('Zapisano lokalnie (zaloguj się, by wysłać na serwer)');
      this.dirty = false;
    } else {
      this.setStatus(`Zapis lokalny OK · serwer: ${remote.code}`);
      this.dirty = false;
    }
    this.saving = false;
  }

  private setStatus(msg: string): void {
    this.statusText?.setText(msg);
  }
}

/** Rozmiar canvasu ikon narzędzi stajni (generateTexture). */
const STABLE_ICON_TEX = 64;

/**
 * Rysuje proceduralną ikonę klocka stajennego (Toca-Boca: kształty + kolory, zero emoji).
 * deska / siano / podkowa / wiadro / koń.
 */
function drawStableBlockIcon(
  g: Phaser.GameObjects.Graphics,
  kind: StableBlockIconKind,
  w: number,
  h: number,
): void {
  g.clear();
  const cx = w / 2;
  const cy = h / 2;

  switch (kind) {
    case 'plank':
      drawIconPlank(g, cx, cy);
      break;
    case 'hay':
      drawIconHay(g, cx, cy);
      break;
    case 'horseshoe':
      drawIconHorseshoe(g, cx, cy);
      break;
    case 'bucket':
      drawIconBucket(g, cx, cy);
      break;
    case 'horse':
    default:
      drawIconHorse(g, cx, cy);
      break;
  }
}

function drawIconPlank(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
): void {
  // deska ogrodzeniowa
  g.fillStyle(0xc4a574, 1);
  g.fillRoundedRect(cx - 22, cy - 18, 44, 36, 6);
  g.fillStyle(0xa1887f, 1);
  g.fillRect(cx - 18, cy - 8, 36, 4);
  g.fillRect(cx - 18, cy + 4, 36, 4);
  g.lineStyle(2, 0xffffff, 0.9);
  g.strokeRoundedRect(cx - 22, cy - 18, 44, 36, 6);
}

function drawIconHay(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
): void {
  // bela siana
  g.fillStyle(0xf0e68c, 1);
  g.fillEllipse(cx, cy + 4, 48, 36);
  g.fillStyle(0xe6d36a, 1);
  g.fillEllipse(cx, cy - 6, 40, 28);
  g.fillStyle(0xd4c05a, 0.85);
  for (let i = -2; i <= 2; i++) {
    g.fillRect(cx + i * 8 - 1, cy - 14, 2, 28);
  }
  g.lineStyle(2, 0xffffff, 0.85);
  g.strokeEllipse(cx, cy + 4, 48, 36);
}

function drawIconHorseshoe(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
): void {
  // podkowa U (grube kreski + końcówki) — bez emoji
  g.lineStyle(9, 0x90a4ae, 1);
  g.lineBetween(cx - 14, cy - 10, cx - 14, cy + 10);
  g.lineBetween(cx + 14, cy - 10, cx + 14, cy + 10);
  g.lineBetween(cx - 14, cy + 10, cx, cy + 18);
  g.lineBetween(cx + 14, cy + 10, cx, cy + 18);
  g.fillStyle(0xb0bec5, 1);
  g.fillCircle(cx - 14, cy - 10, 5);
  g.fillCircle(cx + 14, cy - 10, 5);
  g.lineStyle(2, 0xffffff, 0.9);
  g.strokeCircle(cx - 14, cy - 10, 5);
  g.strokeCircle(cx + 14, cy - 10, 5);
}

function drawIconBucket(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
): void {
  // wiadro / koryto
  g.fillStyle(0x8d6e63, 1);
  g.fillRoundedRect(cx - 16, cy - 4, 32, 26, 6);
  g.fillStyle(0xa1887f, 1);
  g.fillEllipse(cx, cy - 4, 36, 14);
  // uchwyt (prosty łuk z linii)
  g.lineStyle(3, 0x795548, 1);
  g.lineBetween(cx - 14, cy - 6, cx - 10, cy - 20);
  g.lineBetween(cx - 10, cy - 20, cx + 10, cy - 20);
  g.lineBetween(cx + 10, cy - 20, cx + 14, cy - 6);
  g.lineStyle(2, 0xffffff, 0.85);
  g.strokeRoundedRect(cx - 16, cy - 4, 32, 26, 6);
}

function drawIconHorse(
  g: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
): void {
  // prosty koń (głowa + grzywa)
  g.fillStyle(0xa1887f, 1);
  g.fillEllipse(cx + 4, cy + 4, 36, 28);
  g.fillEllipse(cx - 14, cy - 6, 22, 18);
  // ucho
  g.fillTriangle(cx - 18, cy - 18, cx - 10, cy - 10, cx - 20, cy - 8);
  // grzywa
  g.fillStyle(0x6d4c41, 1);
  g.fillEllipse(cx - 6, cy - 14, 14, 10);
  // oko
  g.fillStyle(0xffffff, 1);
  g.fillCircle(cx - 18, cy - 8, 3);
  g.fillStyle(0x3e2723, 1);
  g.fillCircle(cx - 19, cy - 8, 1.5);
  // pysk
  g.fillStyle(0xbcaaa4, 1);
  g.fillEllipse(cx - 22, cy - 2, 12, 8);
}

export default StableScene;
